import { useState, useCallback, useEffect } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  SafeAreaView,
  Image,
  ActivityIndicator,
  Platform,
  Linking,
  useWindowDimensions,
} from "react-native";
import { useFocusEffect, useRouter } from "expo-router";
import { useAuth } from "../lib/auth";
import { getDogs, getDogStats } from "../lib/api";
import { Ionicons } from "@expo/vector-icons";
import type { Dog, DogStats } from "../lib/types";

type DogWithStats = {
  dog: Dog;
  stats: DogStats | null;
};

// Store promo banner — gated to these accounts only while we trial it.
// Add more emails (or swap to a server flag) when we roll it out to everyone.
const AD_PREVIEW_EMAILS = ["gymboslice@gmail.com"];
const STORE_URL = "https://dashhound.app/shop";

// Rotating 3:1 promo banners. Each creative is a 1080×360 (3:1) image and links
// to its own URL; the banner cross-fades between them every ROTATE_MS.
const PROMO_BANNERS = [
  {
    img: "https://dashhound.app/ad-banners/dashhound-store-3x1-v4.png",
    url: STORE_URL,
    label: "Dash-Hound Store — shop tees and bandanas",
  },
  {
    img: "https://dashhound.app/ad-banners/dashhound-banner-pawsitive-3x1.png",
    url: "https://pawsitivemotionk9.com/",
    label: "Pawsitive Motion Canine Rehabilitation — Helping Dogs Improve",
  },
];
const ROTATE_MS = 6000;

function StorePromoBanner() {
  // Explicit width/height so the 3:1 banner fills the row identically on web AND
  // native. (An aspectRatio-only container did not stretch to full width on native.)
  const { width } = useWindowDimensions();
  const w = width - 40; // dashboard ScrollView has padding: 20 on each side
  const h = w / 3; // 3:1 creative
  const [idx, setIdx] = useState(0);
  // Rotate through the banners on a timer.
  useEffect(() => {
    if (PROMO_BANNERS.length < 2) return;
    const id = setInterval(() => setIdx((p) => (p + 1) % PROMO_BANNERS.length), ROTATE_MS);
    return () => clearInterval(id);
  }, []);
  const active = PROMO_BANNERS[idx];
  return (
    <TouchableOpacity
      activeOpacity={0.85}
      onPress={() => Linking.openURL(active.url)}
      style={{
        alignSelf: "center",
        width: w,
        height: h,
        borderRadius: 16,
        marginBottom: 16,
        overflow: "hidden",
        backgroundColor: "#1B2A4A",
      }}
    >
      {/* Both images stay mounted so switching never triggers a reload flash. */}
      {PROMO_BANNERS.map((b, i) => (
        <Image
          key={b.img}
          source={{ uri: b.img }}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: w,
            height: h,
            opacity: i === idx ? 1 : 0,
          }}
          resizeMode="cover"
          accessibilityLabel={b.label}
        />
      ))}
    </TouchableOpacity>
  );
}

function runsAwayLabel(stats: DogStats): string {
  if (!stats.nextTitle) return stats.currentTitle ? "All titles earned!" : "Start running!";
  if (stats.totalRuns === 0) return `${stats.pointsToNextTitle.toFixed(1)} pts to ${stats.nextTitle}`;
  // Use avgPointsPerRun (last 5 logged runs only — never includes historical AKC backfill,
  // so the projection is honest for dogs whose historicalPoints inflate totalPoints).
  const avg = stats.avgPointsPerRun ?? 0;
  const runsNeeded = avg > 0 ? Math.ceil(stats.pointsToNextTitle / avg) : null;
  if (runsNeeded) {
    return `${stats.pointsToNextTitle.toFixed(1)} pts and ~${runsNeeded} run${runsNeeded !== 1 ? "s" : ""} to ${stats.nextTitle}`;
  }
  return `${stats.pointsToNextTitle.toFixed(1)} pts to ${stats.nextTitle}`;
}

function StatRow({ label, value }: { label: string; value: string }) {
  return (
    <View
      style={{
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        paddingVertical: 8,
        borderBottomWidth: 1,
        borderBottomColor: "#F4F6F8",
      }}
    >
      <Text style={{ fontSize: 15, color: "#6B7280" }}>{label}</Text>
      <Text style={{ fontSize: 15, fontWeight: "700", color: "#F5A623" }}>{value}</Text>
    </View>
  );
}

function DogStatCard({ dog, stats, onViewRuns }: { dog: Dog; stats: DogStats | null; onViewRuns: () => void }) {
  return (
    <View
      style={{
        backgroundColor: "#FFFFFF",
        borderRadius: 16,
        marginBottom: 16,
        borderWidth: 1,
        borderColor: "#E5E7EB",
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.06,
        shadowRadius: 6,
        elevation: 3,
        overflow: "hidden",
      }}
    >
      {/* Dog header */}
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          padding: 16,
          borderBottomWidth: 1,
          borderBottomColor: "#F4F6F8",
        }}
      >
        {dog.photo ? (
          <Image
            source={{ uri: dog.photo }}
            style={{ width: 56, height: 56, borderRadius: 28, marginRight: 14 }}
          />
        ) : (
          <View
            style={{
              width: 56,
              height: 56,
              borderRadius: 28,
              backgroundColor: "#F0F2F5",
              alignItems: "center",
              justifyContent: "center",
              marginRight: 14,
            }}
          >
            <Ionicons name="paw" size={26} color="#1B2A4A" />
          </View>
        )}
        <View style={{ flex: 1 }}>
          <Text style={{ fontSize: 18, fontWeight: "800", color: "#1B2A4A" }}>{dog.name}</Text>
          <Text style={{ fontSize: 15, color: "#6B7280", marginTop: 1 }}>{dog.breed}</Text>
          {dog.registeredName ? (
            <Text style={{ fontSize: 12, color: "#6B7280", fontStyle: "italic", marginTop: 1 }}>
              {dog.registeredName}
            </Text>
          ) : null}
          {dog.akcRegistrationNumber ? (
            <Text style={{ fontSize: 13, color: "#9CA3AF", marginTop: 1 }}>
              AKC# {dog.akcRegistrationNumber}
            </Text>
          ) : null}
        </View>
      </View>

      {/* Stats */}
      <View style={{ paddingHorizontal: 16, paddingTop: 4, paddingBottom: 8 }}>
        {stats === null ? (
          <View style={{ alignItems: "center", paddingVertical: 16 }}>
            <ActivityIndicator size="small" color="#1B2A4A" />
          </View>
        ) : (
          <>
            <StatRow
              label="Best 3 Avg (MPH)"
              value={stats.bestThreeAvgSpeed > 0 ? stats.bestThreeAvgSpeed.toFixed(2) : "—"}
            />
            <StatRow
              label="Current Title"
              value={stats.currentTitle ?? "None yet"}
            />
            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
                paddingVertical: 8,
              }}
            >
              <Text style={{ fontSize: 15, color: "#6B7280", flex: 1 }}>Next Title</Text>
              <Text
                style={{
                  fontSize: 15,
                  fontWeight: "700",
                  color: "#F5A623",
                  flex: 2,
                  textAlign: "right",
                  flexShrink: 1,
                }}
                numberOfLines={2}
              >
                {runsAwayLabel(stats)}
              </Text>
            </View>
          </>
        )}
      </View>

      {/* View Profile button */}
      <TouchableOpacity
        onPress={onViewRuns}
        style={{
          backgroundColor: "#1B2A4A",
          marginHorizontal: 16,
          marginBottom: 16,
          paddingVertical: 10,
          borderRadius: 10,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
        }}
      >
        <Text style={{ color: "#FFFFFF", fontWeight: "600", fontSize: 14 }}>View Profile</Text>
        <Ionicons name="arrow-forward" size={14} color="#FFFFFF" />
      </TouchableOpacity>
    </View>
  );
}

export default function DashboardScreen() {
  const { user } = useAuth();
  const router = useRouter();
  const [dogsWithStats, setDogsWithStats] = useState<DogWithStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const firstName = user?.name?.split(" ")[0] ?? "there";
  // Rolled out to everyone 2026-07-07 (was gated to AD_PREVIEW_EMAILS during trial).
  const showStoreAd = true;

  async function fetchAll() {
    try {
      const dogs = await getDogs();
      setError("");
      // Seed the list immediately with null stats so cards render fast
      setDogsWithStats(dogs.map((dog) => ({ dog, stats: null })));

      // Fetch stats for each dog in parallel
      const statsResults = await Promise.allSettled(
        dogs.map((dog) => getDogStats(dog.id))
      );

      setDogsWithStats(
        dogs.map((dog, i) => ({
          dog,
          stats: statsResults[i].status === "fulfilled" ? statsResults[i].value : null,
        }))
      );
    } catch (err: any) {
      setError(err.message || "Failed to load dogs");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useFocusEffect(
    useCallback(() => {
      setLoading(true);
      fetchAll();
    }, [])
  );

  function onRefresh() {
    setRefreshing(true);
    fetchAll();
  }

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: "#F4F6F8" }}>
      <ScrollView
        contentContainerStyle={{ padding: 20, paddingBottom: 60 }}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      >

        {error ? (
          <View style={{ backgroundColor: "#fef2f2", padding: 12, borderRadius: 8, marginBottom: 12 }}>
            <Text style={{ color: "#dc2626", textAlign: "center" }}>{error}</Text>
          </View>
        ) : null}

        {showStoreAd ? <StorePromoBanner /> : null}

        {loading && dogsWithStats.length === 0 ? (
          <View style={{ alignItems: "center", paddingVertical: 60 }}>
            <ActivityIndicator size="large" color="#1B2A4A" />
            <Text style={{ color: "#6B7280", marginTop: 12 }}>Loading your dogs...</Text>
          </View>
        ) : dogsWithStats.length === 0 ? (
          <View
            style={{
              alignItems: "center",
              paddingVertical: 48,
              backgroundColor: "#FFFFFF",
              borderRadius: 16,
              borderWidth: 2,
              borderColor: "#E5E7EB",
              borderStyle: "dashed",
            }}
          >
            <Ionicons name="paw-outline" size={56} color="#D1D5DB" />
            <Text style={{ fontSize: 18, fontWeight: "700", color: "#374151", marginTop: 16 }}>
              No dogs yet
            </Text>
            <Text style={{ fontSize: 14, color: "#6B7280", marginTop: 6, textAlign: "center", paddingHorizontal: 24 }}>
              Add your first dog to start tracking Fast CAT runs
            </Text>
            <TouchableOpacity
              onPress={() => router.push("/(tabs)/dogs/add" as any)}
              style={{
                backgroundColor: "#1B2A4A",
                paddingHorizontal: 24,
                paddingVertical: 12,
                borderRadius: 10,
                marginTop: 20,
              }}
            >
              <Text style={{ color: "#FFFFFF", fontWeight: "600", fontSize: 15 }}>
                Add Your First Dog
              </Text>
            </TouchableOpacity>
          </View>
        ) : (
          dogsWithStats.map(({ dog, stats }) => (
            <DogStatCard
              key={dog.id}
              dog={dog}
              stats={stats}
              onViewRuns={() => router.push(`/(tabs)/dogs/${dog.id}` as any)}
            />
          ))
        )}

      </ScrollView>
    </SafeAreaView>
  );
}
